import { observer, useLocalObservable } from 'mobx-react';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { SPOTLIGHT_DEFINITIONS } from '../constants/spotlightDefinitions';
import { FILTER_DIMENSIONS, FILTER_DIMENSION_GROUPS } from '../constants/filterDimensions';
import {
  COLUMN_FILTER_TYPES,
  COLUMN_FILTER_TYPE_BY_KEY,
  COLUMN_LABEL_BY_KEY
} from '../constants/columnFilterTypes';
import { hasAnyActiveFilter } from '../constants/exploreFilterEngine';
import { cloneFilterValue, isEmptyFilterValue } from '../constants/filterValue';
import SpotlightChip from './SpotlightChip';
import FilterEditorSwitch from './FilterEditorSwitch';
import useModalA11y from '../../../../hooks/useModalA11y';
import { hasKnownRegisterStatus } from '../../../../utils/companyStatus';

/**
 * Unified-filter model — the single "Add filter" panel that consolidates the
 * three former filter surfaces (spotlights bar, per-column ▾, pill rail) into
 * one two-pane popover: quick presets on top, a dimension rail on the left, and
 * the matching editor on the right. It stays open across edits so an analyst can
 * add several filters in one pass, and shows a live "N of M match" count.
 *
 * Commit model (see the ticket): keyword dimensions commit LIVE (pill-style)
 * via `setColumnFilter`; numeric / controlled / location dimensions use a local
 * draft with an explicit Apply, so a half-typed range never filters the list.
 *
 * Rendered next to the `[+ Add filter]` toolbar button inside a
 * `position: relative` anchor; returns null when closed. Reads/writes the shared
 * SearchResults store, so the Projects results store (same shape) can mount it
 * too.
 */
const AddFilterPanel = observer(({ store, variant = 'desktop' }) => {
  const panelRef = useRef(null);
  const open = store.isAddFilterPanelOpen;
  // Defer spotlightCounts — the getter walks every company × spotlight and was
  // sync on open (TD-2626 Add-filter lag). Show chips at 0 until after paint.
  const [presetCounts, setPresetCounts] = useState(() => ({}));
  const isMobile = variant === 'mobile';
  const dialogRef = useRef(null);

  // Mobile renders as a native <dialog> bottom-sheet: showModal() (driven by
  // useModalA11y) puts it in the browser top layer, which escapes the
  // transformed result / tab containers that trapped the old position:fixed
  // panel and dropped it into the page flow (TD-2756). The hook owns the
  // ::backdrop scrim, Esc, the focus trap and the ref-counted body scroll-lock.
  // Desktop keeps the anchored popover with the outside-click/Esc listeners below.
  useModalA11y({
    isOpen: open && isMobile,
    onClose: () => store.closeAddFilterPanel(),
    dialogRef
  });

  // Outside-click → close. Capture-phase + explicit exemptions for the toolbar
  // trigger and the column ▾ (both of which open this panel), so toggling
  // open→closed by re-clicking a trigger works without racing the re-open.
  // Mirrors ColumnFilterPopover's listener.
  useEffect(() => {
    // Desktop-only: the mobile <dialog> handles light-dismiss via useModalA11y.
    if (!open || isMobile) return undefined;
    const onClick = (e) => {
      if (!panelRef.current) return;
      if (panelRef.current.contains(e.target)) return;
      if (e.target?.closest?.('.add-filter-trigger')) return;
      if (e.target?.closest?.('.column-filter-trigger')) return;
      store.closeAddFilterPanel();
    };
    document.addEventListener('click', onClick, { capture: true });
    return () => document.removeEventListener('click', onClick, { capture: true });
  }, [open, store, isMobile]);

  // ESC → close.
  useEffect(() => {
    if (!open || isMobile) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') store.closeAddFilterPanel();
    };
    document.addEventListener('keydown', onKey, { capture: true });
    return () => document.removeEventListener('keydown', onKey, { capture: true });
  }, [open, store, isMobile]);

  // After paint: pull honest spotlight counts (and refresh when filters change).
  const resultsLen = store.results?.length ?? 0;
  const spotlightSize = store.activeSpotlights?.size ?? 0;
  const filterKey = Object.keys(store.columnFilters || {})
    .sort((a, b) => a.localeCompare(b))
    .join(',');
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const raf = requestAnimationFrame(() => {
      const next = store.spotlightCounts;
      if (!cancelled) setPresetCounts(next);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [open, store, resultsLen, spotlightSize, filterKey]);

  if (!open) return null;

  // Non-excluded base for the editors' value lists / numeric extents.
  // `sortedResults` is the getter both the search and project stores expose
  // (sort order is irrelevant to ranking / extents), so the panel is portable.
  const results = store.sortedResults;
  const excludedIds = store.categoryExcludedSpotlightIds;
  const presets = SPOTLIGHT_DEFINITIONS.filter((d) => !excludedIds?.has(d.id));

  const total = store.sortedResults.length;
  const matched = store.filteredResults.length;
  const hasFilter = hasAnyActiveFilter(store.activeSpotlights, store.columnFilters);

  const isDimensionOffered = (key) => {
    if (!COLUMN_FILTER_TYPE_BY_KEY[key]) return false;
    // Profiler Lite has no EBIT-margin data (withheld server-side),
    // so don't offer it as a filter dimension. CAGR stays available.
    if (store.isProfilerLiteOnly && key === 'ebitPercentage') return false;
    // A result set where no company has a known register status (e.g. a fresh
    // industry-code search until TD-3184) would offer only "Unknown". An active filter
    // stays reachable so it can still be cleared.
    // Keyed on the full result set (`store.results`), as the register status sorts are, so
    // the filter and the sorts are offered or withheld together.
    if (key === 'registerStatus') {
      return !!store.columnFilters[key] || hasKnownRegisterStatus(store.results);
    }
    return true;
  };

  // A dimension chosen earlier can stop being offered (the set changed); fall back to the
  // first offered one rather than show an editor the rail no longer lists.
  const activeDimension =
    (isDimensionOffered(store.addFilterDimension) && store.addFilterDimension) ||
    FILTER_DIMENSIONS.find((d) => isDimensionOffered(d.key))?.key ||
    null;

  const panelInner = (
    <>
      <div className="add-filter-panel-head">
        <span className="add-filter-panel-title">Add filter</span>
        <button
          type="button"
          className="add-filter-panel-done"
          onClick={() => store.closeAddFilterPanel()}
        >
          Done
        </button>
      </div>

      <div className="add-filter-panel-presets">
        <span className="add-filter-panel-presets-label">Quick filter</span>
        {presets.map((def) => (
          <SpotlightChip
            key={def.id}
            definition={def}
            count={presetCounts[def.id] || 0}
            active={store.activeSpotlights.has(def.id)}
            onClick={() => store.toggleSpotlight(def.id)}
          />
        ))}
      </div>

      <div className="add-filter-panel-body">
        <div className="add-filter-panel-rail">
          {FILTER_DIMENSION_GROUPS.map((group) => {
            const dims = group.keys.filter(isDimensionOffered);
            if (!dims.length) return null;
            return (
              <div className="add-filter-rail-group" key={group.label}>
                <div className="add-filter-rail-group-label">{group.label}</div>
                {dims.map((key) => (
                  <button
                    key={key}
                    type="button"
                    className={`add-filter-rail-item ${activeDimension === key ? 'selected' : ''}`}
                    aria-pressed={activeDimension === key}
                    onClick={() => store.setAddFilterDimension(key)}
                  >
                    <span className="add-filter-rail-label">{COLUMN_LABEL_BY_KEY[key] ?? key}</span>
                    {store.columnFilters[key] && (
                      <span className="add-filter-rail-dot" aria-label="active" />
                    )}
                  </button>
                ))}
              </div>
            );
          })}
        </div>

        <div className="add-filter-panel-editor">
          {activeDimension && (
            <>
              <div className="add-filter-editor-title">
                {COLUMN_LABEL_BY_KEY[activeDimension] ?? activeDimension}
              </div>
              <PanelEditor
                key={activeDimension}
                store={store}
                dimensionKey={activeDimension}
                results={results}
              />
            </>
          )}
        </div>
      </div>

      <div className="add-filter-panel-count">
        {hasFilter ? (
          <>
            <b>{matched.toLocaleString('en-US')}</b> of {total.toLocaleString('en-US')} match
          </>
        ) : (
          <>
            <b>{total.toLocaleString('en-US')}</b> companies
          </>
        )}
      </div>
    </>
  );

  // Mobile: a native <dialog> bottom-sheet. showModal() (via useModalA11y) puts
  // it in the browser top layer, so `position: fixed` no longer resolves against
  // a transformed result/tab ancestor — the drawer pins to the viewport bottom
  // behind a ::backdrop scrim instead of collapsing into the page flow (TD-2756).
  // The <dialog> is a transparent, full-viewport click-to-dismiss surface; the
  // visible card is `.add-filter-panel--mobile`.
  if (isMobile) {
    return (
      <dialog ref={dialogRef} className="add-filter-panel-sheet" aria-label="Add filter">
        <div className="add-filter-panel add-filter-panel--mobile">{panelInner}</div>
      </dialog>
    );
  }

  return (
    <div
      className="add-filter-panel add-filter-panel--desktop"
      ref={panelRef}
      aria-label="Add filter"
    >
      {panelInner}
    </div>
  );
});

AddFilterPanel.propTypes = {
  store: PropTypes.object.isRequired,
  variant: PropTypes.string
};

/**
 * Editor pane for one dimension. Keyword dimensions commit live; the others use
 * a draft + Apply. Keyed by dimensionKey in the parent so it remounts (fresh
 * draft) when the analyst switches dimensions.
 */
const PanelEditor = observer(({ store, dimensionKey, results }) => {
  if (COLUMN_FILTER_TYPE_BY_KEY[dimensionKey] === COLUMN_FILTER_TYPES.KEYWORD_CLOUD) {
    return (
      <FilterEditorSwitch
        columnKey={dimensionKey}
        value={store.columnFilters[dimensionKey]}
        onChange={(next) => store.setColumnFilter(dimensionKey, next)}
        results={results}
        columnFilters={store.columnFilters}
        activeSpotlights={store.activeSpotlights}
      />
    );
  }
  return <PanelDraftEditor store={store} dimensionKey={dimensionKey} results={results} />;
});

PanelEditor.propTypes = {
  store: PropTypes.object.isRequired,
  dimensionKey: PropTypes.string.isRequired,
  results: PropTypes.array.isRequired
};

const PanelDraftEditor = observer(({ store, dimensionKey, results }) => {
  const committed = store.columnFilters[dimensionKey];
  const draftState = useLocalObservable(() => ({
    draft: cloneFilterValue(committed),
    setDraft: (next) => {
      draftState.draft = next;
    }
  }));

  // Re-sync the draft if the committed value changes underneath the panel
  // (e.g. a preset writes this column). Stable while the analyst edits.
  useEffect(() => {
    draftState.setDraft(cloneFilterValue(committed));
  }, [committed, draftState]);

  const apply = () => {
    store.setColumnFilter(
      dimensionKey,
      isEmptyFilterValue(draftState.draft) ? null : draftState.draft
    );
  };
  const clear = () => {
    store.clearColumnFilter(dimensionKey);
    draftState.setDraft(null);
  };

  return (
    <form
      className="add-filter-editor-form"
      onSubmit={(e) => {
        e.preventDefault();
        apply();
      }}
    >
      <FilterEditorSwitch
        columnKey={dimensionKey}
        value={draftState.draft}
        onChange={draftState.setDraft}
        results={results}
        columnFilters={store.columnFilters}
        activeSpotlights={store.activeSpotlights}
      />
      <div className="add-filter-editor-actions">
        <button
          type="button"
          className="add-filter-editor-clear"
          onClick={clear}
          disabled={isEmptyFilterValue(committed) && isEmptyFilterValue(draftState.draft)}
        >
          Clear
        </button>
        <button
          type="submit"
          className="add-filter-editor-apply"
          // Same empty-both gate as Clear: blank Apply with nothing committed is a
          // no-op affordance (TD-2626 F). Emptying a committed draft must stay enabled
          // so Apply can clear the filter.
          disabled={isEmptyFilterValue(committed) && isEmptyFilterValue(draftState.draft)}
        >
          Apply
        </button>
      </div>
    </form>
  );
});

PanelDraftEditor.propTypes = {
  store: PropTypes.object.isRequired,
  dimensionKey: PropTypes.string.isRequired,
  results: PropTypes.array.isRequired
};

export default AddFilterPanel;
